import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const ActivityFeed = ({ activities, onRefresh }) => {
  const [filterRegistry, setFilterRegistry] = useState('all');
  const [filterEventType, setFilterEventType] = useState('all');

  const registryOptions = [
    { value: 'all', label: 'All Registries' },
    { value: 'verra', label: 'Verra' },
    { value: 'gold-standard', label: 'Gold Standard' },
    { value: 'acr', label: 'ACR' },
    { value: 'art', label: 'ART' },
    { value: 'gcc', label: 'GCC' },
    { value: 'cad-trust', label: 'CAD Trust' }
  ];

  const eventTypeOptions = [
    { value: 'all', label: 'All Events' },
    { value: 'sync', label: 'Sync Events' },
    { value: 'error', label: 'Errors' },
    { value: 'connection', label: 'Connection' },
    { value: 'data-ingestion', label: 'Data Ingestion' }
  ];

  const getEventIcon = (type) => {
    switch (type) {
      case 'sync':
        return 'RefreshCw';
      case 'error':
        return 'AlertCircle';
      case 'connection':
        return 'Link';
      case 'data-ingestion':
        return 'Database';
      default:
        return 'Activity';
    }
  };

  const getEventColor = (type) => {
    switch (type) {
      case 'sync':
        return 'text-primary bg-primary/10';
      case 'error':
        return 'text-error bg-error/10';
      case 'connection':
        return 'text-success bg-success/10';
      case 'data-ingestion':
        return 'text-warning bg-warning/10';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  const filteredActivities = activities?.filter(activity => {
    const registryMatch = filterRegistry === 'all' || activity?.registry === filterRegistry;
    const eventMatch = filterEventType === 'all' || activity?.type === filterEventType;
    return registryMatch && eventMatch;
  });

  return (
    <div className="bg-card border border-border rounded-lg">
      {/* Header */}
      <div className="flex items-center justify-between p-6 border-b border-border">
        <div>
          <h3 className="text-lg font-semibold text-card-foreground">Activity Feed</h3>
          <p className="text-sm text-muted-foreground">Recent synchronization events and system logs</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          iconName="RefreshCw"
          iconPosition="left"
          onClick={onRefresh}
        >
          Refresh
        </Button>
      </div>
      {/* Filters */}
      <div className="flex items-center space-x-4 p-6 border-b border-border bg-muted/20">
        <Select
          placeholder="Filter by registry"
          options={registryOptions}
          value={filterRegistry}
          onChange={setFilterRegistry}
          className="w-48"
        />
        <Select
          placeholder="Filter by event type"
          options={eventTypeOptions}
          value={filterEventType}
          onChange={setFilterEventType}
          className="w-48"
        />
        <div className="text-xs text-muted-foreground">
          {filteredActivities?.length} of {activities?.length} events
        </div>
      </div>
      {/* Activity List */}
      <div className="max-h-96 overflow-y-auto">
        {filteredActivities?.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Icon name="Activity" size={48} className="text-muted-foreground mb-4" />
            <p className="text-muted-foreground">No activities match your filters</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {filteredActivities?.map((activity) => (
              <div key={activity?.id} className="flex items-start space-x-4 p-6 hover:bg-muted/20 transition-smooth">
                <div className={`flex items-center justify-center w-8 h-8 rounded-full ${getEventColor(activity?.type)}`}>
                  <Icon name={getEventIcon(activity?.type)} size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2 mb-1">
                    <span className="text-sm font-medium text-card-foreground">{activity?.title}</span>
                    <span className="text-xs text-muted-foreground px-2 py-1 bg-muted rounded-full">
                      {activity?.registryName}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">{activity?.description}</p>
                  <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                    <span>{activity?.timestamp}</span>
                    {activity?.duration && <span>Duration: {activity?.duration}</span>}
                    {activity?.recordsProcessed && <span>{activity?.recordsProcessed} records</span>}
                  </div>
                </div>
                {activity?.status && (
                  <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                    activity?.status === 'success' ?'bg-success/10 text-success' 
                      : activity?.status === 'failed' ?'bg-error/10 text-error' :'bg-warning/10 text-warning'
                  }`}>
                    {activity?.status}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ActivityFeed;